import React, { useEffect, useState } from 'react';
import type { FC } from 'react';

import { Button } from '../../ui/Button/Button';
import { DateRangePicker, toIsoDate, type DateRange } from '../../ui/DatePicker';
import { Modal, ModalActions, ModalNote } from '../../ui/Modal/Modal';
import { IconDownload } from '../../../assets/icons/icons';
import { downloadFile } from '../../../shared/api/download';
import { supportReportPath } from '../../../entities/support/api/supportApi';
import styles from './ExportRequestsModal.module.scss';

const toApiDate = (value: string): string => value.split('-').reverse().join('-');

const defaultRange = (): DateRange => {
    const end = new Date();
    const start = new Date(end);
    start.setMonth(start.getMonth() - 1);

    return { start: toIsoDate(start), end: toIsoDate(end) };
};

interface ExportRequestsModalProps {
    open: boolean;
    onClose: () => void;
    onNotify: (message: string) => void;
}

export const ExportRequestsModal: FC<ExportRequestsModalProps> = ({ open, onClose, onNotify }) => {
    const [range, setRange] = useState<DateRange>(defaultRange);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setRange(defaultRange());
        setIsLoading(false);
    }, [open]);

    const submit = async (): Promise<void> => {
        const startDate = toApiDate(range.start);
        const endDate = toApiDate(range.end);

        setIsLoading(true);

        try {
            await downloadFile(
                supportReportPath(startDate, endDate),
                `Отчёт по обращениям с ${startDate} по ${endDate}.xlsx`,
            );
            onClose();
        } catch (error) {
            onNotify(error instanceof Error ? error.message : 'Не удалось выгрузить отчёт');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            width={440}
            title="Выгрузка отчёта"
            description="Укажите период — в файл попадут обращения, созданные в эти даты."
            icon={<IconDownload />}
            onClose={onClose}
            footer={
                <>
                    <ModalNote>Формат файла — Excel</ModalNote>
                    <ModalActions>
                        <Button onClick={onClose} disabled={isLoading}>
                            Отмена
                        </Button>
                        <Button variant="primary" disabled={isLoading} onClick={submit}>
                            {isLoading ? 'Готовим файл' : 'Выгрузить'}
                        </Button>
                    </ModalActions>
                </>
            }
        >
            <div className={styles.field}>
                <span className={styles.label}>Период</span>
                <DateRangePicker value={range} onChange={(next) => next && setRange(next)} />
            </div>
        </Modal>
    );
};
